'use client';
import { OnbForm } from '@/components/form';
import { connectedField } from '@/redux/fields';
import { deleteStepVariables, pushStepVariables } from '@/redux/flowSlice';
import { RootState } from '@/redux/store';
import { Accordion, AccordionDetails, AccordionSummary, Button, Stack, TextField, Typography } from '@mui/material';
import {
  ECOMMERCE_PLATFORM_LABELS,
  EcommercePlatformEnum,
  merchantIdentification,
  productCodesConst,
  salesChannelsConst,
  termsAndConditions,
  variables,
} from '@repo/onb-api';
import { Checkbox, COLORS, Dropdown, MaterialIcon, ProvinceDropdowns } from '@repo/ui';
import { cleanHTML, formatter } from '@repo/utils';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function MerchantIdentification({
  stepname: stepName,
  stepData: stepData,
}: {
  stepname?: merchantIdentification['name'];
  stepData?: merchantIdentification['variables'];
}): React.JSX.Element {
  const dispatch = useDispatch();
  const stepVariables = useSelector(
    (state: RootState) => state?.flowdata?.steps?.find((s) => s.name === stepName)?.variables as variables,
  );
  const { salesChannel } = stepVariables || {};
  const isSalesChannelEcommerceOrOmni = salesChannel === 'ECOMMERCE' || salesChannel === 'OMNICHANNEL';
  const { ecommercePlatform } = stepVariables || {};
  const isEcommerceStoreOther = ecommercePlatform === EcommercePlatformEnum.OTHER;
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsState, setTermsState] = useState(false);
  const termsRef = useRef<HTMLButtonElement | null>(null);
  const termsError = termsRef.current?.className?.includes('checkboxError') ?? false;

  const dropdownsRef = useRef<{ validate: () => boolean }>(null);
  const salesChannelOptions = Object.entries(salesChannelsConst).map(([value, label]) => ({
    label: `Tengo una ${label.toLowerCase()}`,
    value,
  }));
  const ecommercePlatformOptions = Object.values(EcommercePlatformEnum).map((value) => ({
    value,
    label: ECOMMERCE_PLATFORM_LABELS[value],
  }));
  let productCodesOptions = Object.entries(productCodesConst).map(([value, label]) => ({ label, value }));
  delete productCodesOptions[1]; // only BNPL available as for now
  delete productCodesOptions[2]; // only BNPL available as for now

  useEffect(() => {
    dispatch(
      deleteStepVariables({
        stepName: stepName!,
        variables: ['ecommercePlatform', 'ecommercePlatformOtherName', 'ecommerceUrl'],
      }),
    );
  }, [dispatch, stepName, salesChannel]);

  return (
    <StepWrapper>
      <Typography variant="h1">Cargá los datos de tu comercio</Typography>
      <OnbForm<merchantIdentification> stepName={stepName!}>
        <Stack my={1} gap={2}>
          <TextField
            label="CUIT"
            autoFocus
            placeholder="Ej: 20-12345678-9"
            slotProps={{ htmlInput: { maxLength: 13, inputMode: 'numeric', 'data-testid': 'taxpayerId' } }}
            {...connectedField({
              stepName,
              name: 'taxpayerId',
              required: true,
              beforeRender: (value) => formatter(value, 'cuit'),
              beforeChange: (value) => value.replaceAll(/\D/g, ''),
              errorHandler: {
                type: 'text',
                errorText: 'Ingresa un CUIT válido',
                validate: (value) => value.length === 13,
              },
            })}
          />
          <TextField
            label="Nombre del comercio"
            placeholder="Ej: Electro Hogar"
            slotProps={{ htmlInput: { inputMode: 'text', 'data-testid': 'tradeName' } }}
            {...connectedField({
              stepName,
              name: 'tradeName',
              required: true,
              //@TODO - Ver cual seria un nombre valido
              // beforeChange: (value) => value.replace(/[^a-zA-Z\s]/g, ''),
              errorHandler: {
                type: 'text',
                errorText: 'El nombre no es válido',
                // validate: (value) => /^[\p{L} ]+$/u.test(value),
              },
            })}
          />
        </Stack>
        <Stack my={1} gap={2}>
          <Typography variant="h2">Dirección legal</Typography>
          <ProvinceDropdowns
            id={stepData?.localityId || stepVariables?.localityId}
            ref={dropdownsRef}
            onBlur={() => dropdownsRef.current?.validate()}
            onCompleted={(id) => {
              if (stepName) {
                if (id) {
                  dispatch(
                    pushStepVariables({
                      stepName,
                      variable: 'localityId',
                      value: id,
                    }),
                  );
                } else {
                  console.error('Error: localityId not found');
                }
              } else {
                console.error('Error: stepName not provided');
              }
            }}
            onDropdownChange={() => {
              if (stepName) {
                dispatch(
                  pushStepVariables({
                    stepName,
                    variable: 'localityId',
                    value: '',
                  }),
                );
              }
            }}
          />
          <TextField
            label="Código postal"
            placeholder="Ej: 1234"
            slotProps={{ htmlInput: { maxLength: 8, inputMode: 'text', 'data-testid': 'postalCode' } }}
            {...connectedField({
              stepName,
              name: 'postalCode',
              required: true,
              beforeRender: (value) => value.replaceAll(/[^a-zA-Z0-9]/g, ''),
              beforeChange: (value) => value.replaceAll(/[^a-zA-Z0-9]/g, ''),
              errorHandler: {
                type: 'text',
                errorText: 'Ingresá un código postal válido',
              },
            })}
          />
          <TextField
            label="Dirección"
            placeholder="Ej: Av. Cabildo"
            slotProps={{ htmlInput: { inputMode: 'text', 'data-testid': 'streetName' } }}
            {...connectedField({
              stepName,
              name: 'streetName',
              required: true,
              errorHandler: {
                type: 'text',
                errorText: 'Ingresá una dirección válida',
              },
            })}
          />
          <TextField
            label="Altura"
            placeholder="Ej: 2487"
            slotProps={{ htmlInput: { inputMode: 'text', 'data-testid': 'streetNumber' } }}
            {...connectedField({
              stepName,
              name: 'streetNumber',
              required: true,
              beforeRender: (value) => value.replaceAll(/\D/g, ''),
              beforeChange: (value) => value.replaceAll(/\D/g, ''),
              errorHandler: {
                type: 'text',
                errorText: 'Ingresá un número válido',
              },
            })}
          />
        </Stack>
        <Stack my={1} gap={2}>
          <Typography variant="h2">Seleccioná tu modalidad de venta</Typography>
          <Dropdown
            label="Modalidad de venta"
            placeholder="Seleccioná una opción"
            options={salesChannelOptions}
            {...connectedField({
              stepName,
              name: 'salesChannel',
              required: true,
              beforeChange: (value) => value,
              errorHandler: {
                type: 'select',
                errorText: 'Seleccioná una modalidad de venta',
              },
            })}
          />
        </Stack>
        <Stack my={1} gap={2} sx={{ display: isSalesChannelEcommerceOrOmni ? 'flex' : 'none' }}>
          <Typography variant="h2">Seleccioná tu plataforma ecommerce</Typography>
          <Dropdown
            label="Plataforma ecommerce"
            placeholder="Seleccioná una opción"
            options={ecommercePlatformOptions}
            {...connectedField({
              stepName,
              name: 'ecommercePlatform',
              required: isSalesChannelEcommerceOrOmni ? true : false,
              beforeChange: (value) => value,
              errorHandler: {
                type: 'select',
                errorText: 'Seleccioná una tienda ecommerce',
              },
            })}
          />
        </Stack>
        <Stack
          mt={1}
          mb={2}
          gap={2}
          sx={{ display: isEcommerceStoreOther && isSalesChannelEcommerceOrOmni ? 'flex' : 'none' }}
        >
          <Typography variant="h2">Si seleccionaste 'Otra', ingresá el nombre de tu plataforma</Typography>
          <TextField
            label="Nombre de la plataforma"
            placeholder="Ej: Tiendanube"
            slotProps={{ htmlInput: { inputMode: 'text', 'data-testid': 'ecommercePlatformOther' } }}
            {...connectedField({
              stepName,
              name: 'ecommercePlatformOtherName',
              required: isEcommerceStoreOther ? true : false,
              //@TODO - Ver cual seria un nombre valido
              // beforeChange: (value) => value.replace(/[^a-zA-Z\s]/g, ''),
              errorHandler: {
                type: 'text',
                errorText: 'El nombre no es válido',
                // validate: (value) => /^[\p{L} ]+$/u.test(value),
              },
            })}
          />
        </Stack>
        <Stack mt={1} mb={2} gap={2} sx={{ display: isSalesChannelEcommerceOrOmni ? 'flex' : 'none' }}>
          <Typography variant="h2">Ingresá la URL de tu tienda</Typography>
          <TextField
            label="URL de la tienda"
            placeholder="Ej: electrohogar.com"
            slotProps={{ htmlInput: { inputMode: 'url', 'data-testid': 'ecommerceUrl' } }}
            {...connectedField({
              stepName,
              name: 'ecommerceUrl',
              required: isSalesChannelEcommerceOrOmni ? true : false,
              // beforeChange: (value) => value.replace(/[^a-zA-Z\s]/g, ''),
              errorHandler: {
                type: 'text',
                errorText: 'Ingresar una url válida',
                // validate: isSalesChannelEcommerceOrOmni
                //   ? (value) => /^[\p{L}0-9-]+\.[\p{L}0-9-]+$/u.test(value)
                //   : undefined,
              },
            })}
          />
        </Stack>
        <Stack mt={1} mb={2} gap={2}>
          <Typography variant="h2">Seleccioná tu plan</Typography>
          <Dropdown
            multiple
            label="Plan"
            placeholder="Seleccioná una opción"
            options={productCodesOptions}
            renderValue={(selected) => {
              const selectedValues = Array.isArray(selected) ? selected : [];
              return selectedValues.length === 0 ? (
                <span style={{ color: COLORS.text.placeholder, fontSize: '1rem' }}>Seleccioná una opción</span>
              ) : (
                <span style={{ fontSize: '1rem' }}>
                  {selectedValues
                    .map((val) => productCodesOptions.find((opt) => opt.value === val)?.label || val)
                    .join(', ')}
                </span>
              );
            }}
            {...connectedField({
              stepName,
              name: 'productCodes',
              required: true,
              beforeRender: (value: string) => (value !== '' ? [...value] : []),
              errorHandler: {
                type: 'select',
                errorText: 'Seleccioná una modalidad de venta',
              },
            })}
          />
        </Stack>
        <Typography variant="h2">Uso de datos</Typography>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '8px !important',
            borderColor: !termsState && termsError ? COLORS.state.error : 'rgba(0, 0, 0, 0.23)',
            py: '0px !important',
          }}
          expanded={termsOpen}
          onChange={() => setTermsOpen(!termsOpen)}
        >
          <AccordionSummary
            sx={{
              py: 0,
              px: 1,
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
            }}
            expandIcon={<MaterialIcon icon="keyboard_arrow_down" size={34} color="primary" />}
          >
            <Checkbox
              ref={(el) => {
                termsRef.current = el;
              }}
              border="none"
              {...connectedField({
                checkbox: true,
                stepName,
                name: 'hasTermsAccepted',
                required: true,
                beforeChange: (value) => {
                  setTermsState(!!value);
                  return !!value;
                },
                errorHandler: {
                  type: 'checkbox',
                },
              })}
            />
            <Typography variant="body2" display="flex" alignItems={'center'} width="70%">
              Acepto el uso de mis datos
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body3">
              <div dangerouslySetInnerHTML={{ __html: cleanHTML(termsAndConditions || '') }} />
            </Typography>
            {termsOpen && (
              <Button variant="text" sx={{ p: 2, mt: 1 }} fullWidth onClick={() => setTermsOpen(false)}>
                Mostrar menos
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
        {!termsState && termsError && (
          <Typography variant="body2" sx={{ textAlign: 'center' }} color="error">
            Aceptá el uso de tus datos para continuar
          </Typography>
        )}
      </OnbForm>
    </StepWrapper>
  );
}
